// Shoulder Joint Template Configuration

export const shoulderTemplate = {
  id: 'SHOULDER',
  name: 'Shoulder Joint',
  icon: '🦴',
  dominantSide: 'hand',

  // Form field definitions
  fields: {
    // Demographics
    dominantHand: {
      type: 'radio',
      label: 'Which hand do you write with?',
      options: ['Right', 'Left'],
      required: true
    },

    // Injury details
    injuredSide: {
      type: 'radio',
      label: 'Which shoulder is the problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    injuryDate: {
      type: 'date',
      label: 'When did your shoulder problem start?',
      required: true
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Describe how the injury occurred:',
      placeholder: 'Please describe the circumstances of your injury',
      rows: 3
    },

    mechanism: {
      type: 'checkbox-multiple',
      label: 'How did your shoulder problem start?',
      options: [
        'Fell on outstretched hand',
        'Got hit directly on the shoulder',
        'Throwing (like cricket or baseball)',
        'Lifting something heavy',
        'Repetitive work with arms above head',
        'Shoulder popped out of place',
        'It came on gradually (no specific injury)',
        'Other'
      ]
    },

    mechanismOther: {
      type: 'text',
      label: 'Please describe what happened:',
      placeholder: 'Tell us how you were injured',
      showWhen: { field: 'mechanism', includes: 'Other' }
    },

    // Pain details
    painSite: {
      type: 'text',
      label: 'Where is your pain?',
      placeholder: 'e.g., Front of shoulder, back, side, top, all over',
      section: 'pain'
    },

    painRadiation: {
      type: 'text',
      label: 'Does your pain spread anywhere else?',
      placeholder: 'e.g., To neck, upper arm, elbow, hand',
      section: 'pain'
    },

    painCharacter: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Dull', 'Sharp', 'Aching', 'Burning', 'Stabbing', 'Throbbing'],
      section: 'pain'
    },

    aggravatingFactors: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Reaching above your head',
        'Lifting things',
        'Reaching behind your back',
        'Lying on your shoulder',
        'Turning your arm',
        'Pushing or pulling',
        'Carrying things',
        'Other'
      ],
      section: 'pain'
    },

    aggravatingFactorsOther: {
      type: 'text',
      label: 'Other aggravating factors:',
      placeholder: 'Please specify',
      showWhen: { field: 'aggravatingFactors', includes: 'Other' }
    },

    relievingFactors: {
      type: 'checkbox-multiple',
      label: 'What makes your pain better?',
      options: [
        'Rest',
        'Certain positions',
        'Heat',
        'Ice',
        'Pain medication',
        'Avoiding reaching above your head',
        'Other'
      ],
      section: 'pain'
    },

    relievingFactorsOther: {
      type: 'text',
      label: 'Other relieving factors:',
      placeholder: 'Please specify',
      showWhen: { field: 'relievingFactors', includes: 'Other' }
    },

    associatedSymptoms: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these other symptoms?',
      options: [
        'Clicking or popping',
        'Shoulder getting stuck',
        'Weakness',
        'Numbness or tingling',
        'Feeling like your shoulder might pop out',
        'Pain at night'
      ],
      section: 'pain'
    },

    // Treatment fields
    hadPhysio: {
      type: 'radio',
      label: 'Have you had physiotherapy for this injury?',
      options: ['Yes', 'No']
    },

    physioWhere: {
      type: 'text',
      label: 'Where did you have physiotherapy?',
      placeholder: 'e.g., PhysioFirst Hamilton, ACC Physio Clinic',
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    physioWho: {
      type: 'text',
      label: 'Who was your physiotherapist?',
      placeholder: 'e.g., John Smith, Sarah Jones',
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    physioWhat: {
      type: 'textarea',
      label: 'What treatment did they provide?',
      placeholder: 'e.g., Exercises, massage, dry needling, ultrasound',
      rows: 2,
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    physioHelped: {
      type: 'radio',
      label: 'Did the physiotherapy help?',
      options: ['Yes, significantly', 'Yes, somewhat', 'No improvement', 'Made it worse'],
      showWhen: { field: 'hadPhysio', includes: 'Yes' }
    },

    medications: {
      type: 'textarea',
      label: 'What medications have you tried?',
      placeholder: 'e.g., Paracetamol, ibuprofen, voltaren - did they help?',
      rows: 2
    },

    imaging: {
      type: 'radio',
      label: 'Have you had any imaging (X-ray, MRI, ultrasound)?',
      options: ['Yes', 'No']
    },

    imagingType: {
      type: 'checkbox-multiple',
      label: 'What type of imaging have you had?',
      options: ['X-ray', 'MRI', 'Ultrasound', 'CT scan', 'Other'],
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhere: {
      type: 'text',
      label: 'Where was the imaging done?',
      placeholder: 'e.g., Waikato Hospital, Pacific Radiology',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingWhen: {
      type: 'date',
      label: 'When was the imaging done?',
      showWhen: { field: 'imaging', includes: 'Yes' }
    },

    imagingResults: {
      type: 'textarea',
      label: 'What were the results?',
      placeholder: 'e.g., Normal, rotator cuff tear, impingement',
      rows: 2,
      showWhen: { field: 'imaging', includes: 'Yes' }
    }
  },

  // Section definitions
  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true,
      customFields: ['dominantHand']
    },
    {
      id: 'injury',
      title: '2. How Your Shoulder Problem Started',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injuredSide', 'mechanism', 'mechanismOther', 'injuryDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painSite', 'painCharacter', 'painSeverity', 'painRadiation',
               'aggravatingFactors', 'aggravatingFactorsOther', 'relievingFactors', 'relievingFactorsOther', 'associatedSymptoms']
    },
    {
      id: 'stiffness',
      title: '4. Stiffness / Movement',
      component: 'StiffnessSection',
      enabled: true,
      movements: [
        { id: 'flexion', label: 'Reaching forward', icon: '🖐️' },
        { id: 'abduction', label: 'Lifting arm out to the side', icon: '↔️' },
        { id: 'internalRotation', label: 'Putting hand behind your back', icon: '🔄' },
        { id: 'externalRotation', label: 'Putting hand behind your head', icon: '🔄' }
      ]
    },
    {
      id: 'instability',
      title: '5. Shoulder Popping Out',
      component: 'InstabilitySection',
      enabled: true,
      type: 'shoulder',
      fields: [
        {
          id: 'dislocationHistory',
          type: 'textarea',
          label: 'Has your shoulder ever popped out of place (fully or partly)?',
          placeholder: 'Tell us what happened',
          rows: 3
        },
        {
          id: 'numberOfEpisodes',
          type: 'text',
          label: 'How many times has this happened?',
          placeholder: 'e.g., Once, twice, many times'
        },
        {
          id: 'circumstances',
          type: 'textarea',
          label: 'What were you doing when it happened?',
          placeholder: 'e.g., Playing rugby, reaching up, very little force needed',
          rows: 3
        },
        {
          id: 'direction',
          type: 'checkbox-multiple',
          label: 'Which way did your shoulder go (if you know)?',
          options: ['Forward', 'Backward', 'Downward', 'Multiple directions', 'I don\'t know']
        },
        {
          id: 'recurrence',
          type: 'radio',
          label: 'Does it keep happening?',
          options: ['Yes', 'No']
        }
      ]
    },
    {
      id: 'swelling',
      title: '6. Swelling / Changes in Shape',
      component: 'SwellingSection',
      enabled: true,
      simple: true
    },
    {
      id: 'functional',
      title: '7. What You Can and Can\'t Do',
      component: 'FunctionalLimitationSection',
      enabled: true,
      activities: [
        'Getting dressed (reaching behind)',
        'Brushing or washing your hair',
        'Lifting things above your head',
        'Reaching behind your back (like fastening a bra or tucking in a shirt)',
        'Sports or work activities',
        'Sleeping on your sore shoulder',
        'Throwing'
      ]
    },
    {
      id: 'treatment',
      title: '8. Treatment So Far',
      component: 'GenericSection',
      enabled: true,
      fields: ['hadPhysio', 'physioWhere', 'physioWho', 'physioWhat', 'physioHelped', 'medications', 'imaging', 'imagingType', 'imagingWhere', 'imagingWhen', 'imagingResults']
    },
    {
      id: 'pastHistory',
      title: '9. Your Shoulder History',
      component: 'PastHistorySection',
      enabled: true,
      specificQuestions: [
        {
          id: 'previousShoulderInjury',
          label: 'Have you injured this shoulder before or had shoulder surgery?',
          type: 'textarea',
          placeholder: 'Include when it happened, what the injury was, and how it turned out',
          rows: 3
        },
        {
          id: 'fractures',
          label: 'Have you ever broken any bones near your shoulder or had it pop out?',
          type: 'textarea',
          placeholder: 'e.g., Broken collarbone, broken arm bone, shoulder dislocations',
          rows: 2
        },
        {
          id: 'neckPain',
          label: 'Do you have any neck pain or neck problems?',
          type: 'textarea',
          placeholder: 'Sometimes neck problems can cause shoulder pain',
          rows: 2
        },
        {
          id: 'otherJointInvolvement',
          label: 'Do you have problems with other joints?',
          type: 'textarea',
          placeholder: 'Other shoulder, elbows, knees - this might suggest a general joint condition',
          rows: 2
        }
      ]
    },
    {
      id: 'medical',
      title: '10. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true
    },    {
      id: 'exerciseVitalSign',
      title: '11. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '12. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  // Letter generation templates
  letterTemplate: {
    mechanismLanguage: {
      'Fell on outstretched hand': 'a fall onto an outstretched hand',
      'Got hit directly on the shoulder': 'a direct blow to the shoulder',
      'Throwing (like cricket or baseball)': 'overhead throwing activities',
      'Lifting something heavy': 'lifting a heavy object',
      'Repetitive work with arms above head': 'repetitive overhead work',
      'Shoulder popped out of place': 'a shoulder dislocation',
      'It came on gradually (no specific injury)': 'a gradual onset with no specific traumatic event'
    },

    examinationGuidance: `
Based on the clinical information provided, examination findings would typically include:

INSPECTION: Assessment of shoulder symmetry, muscle bulk (deltoid, rotator cuff), any obvious deformity or asymmetry, scapular positioning.

PALPATION: Tenderness over the AC joint, greater tuberosity, biceps tendon, and rotator cuff insertion points.

RANGE OF MOTION:
- Active and passive forward flexion, abduction, and rotation
- Comparison with contralateral side
- Pain arc assessment

STRENGTH TESTING:
- Rotator cuff strength (supraspinatus, infraspinatus, subscapularis)
- Deltoid strength
- Biceps strength

SPECIAL TESTS:
- Impingement tests (Neer's, Hawkins-Kennedy)
- Rotator cuff tests (empty can, external rotation lag)
- Instability tests (apprehension, relocation, load and shift)
- Labral tests (O'Brien's, anterior slide)

NEUROVASCULAR: Radial pulse, sensation in axillary nerve distribution
`,

    commonDiagnoses: [
      'Rotator cuff tendinopathy',
      'Subacromial impingement syndrome',
      'Rotator cuff tear (partial or full thickness)',
      'Glenohumeral instability',
      'Anterior shoulder dislocation (recurrent)',
      'AC joint pathology',
      'Adhesive capsulitis (frozen shoulder)',
      'Labral tear (SLAP lesion)',
      'Biceps tendinopathy',
      'Calcific tendinitis'
    ]
  }
};

export default shoulderTemplate;
